import React, { useState, useEffect, Suspense, lazy, ReactNode } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Select,
  DatePicker,
  Button,
  Tabs,
  Space,
  Tag,
  Typography,
  Progress,
  Avatar,
  Radio,
  Tooltip,
  Divider,
  Badge,
  List,
  Timeline,
  Alert,
  Empty,
  Spin,
} from 'antd';
import {
  UserOutlined,
  RiseOutlined,
  FallOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  ReloadOutlined,
  ExportOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  FireOutlined,
  HeartOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// 声明图表组件，但不立即导入
let LineChart: any = null;
let ColumnChart: any = null;
let PieChart: any = null;

// 安全的图表渲染包装器
interface SafeChartProps {
  children: ReactNode;
  height?: number;
}

const SafeChart: React.FC<SafeChartProps> = ({ children, height = 300 }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // 在组件挂载后，安全地加载图表库
    if (!LineChart) {
      import('@ant-design/plots').then(({ Line, Column, Pie }) => {
        LineChart = Line;
        ColumnChart = Column;
        PieChart = Pie;
        setLoaded(true);
      }).catch(e => {
        console.error('加载图表组件失败:', e);
      });
    } else {
      setLoaded(true);
    }
  }, []);

  if (!loaded) {
    return (
      <div style={{ height, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin tip="图表加载中..." />
      </div>
    );
  }

  return <div style={{ height }}>{children}</div>;
};

// 会员留存数据接口
interface MemberRetention {
  id: string;
  name: string;
  avatar: string;
  level: string;
  bindingStatus: 'bound' | 'unbound';
  butler?: string;
  bindTime?: string;
  firstOrderDate: string;
  lastOrderDate: string;
  totalOrders: number;
  totalSpent: number;
  repeatPurchaseRate: number;
  ltv: number;
  retentionScore: number;
  churnRisk: 'low' | 'medium' | 'high';
  nextPredictedOrder?: string;
  inactivityDays: number;
}

// 复购率趋势数据接口
interface RepurchaseTrend {
  month: string;
  withButler: number;
  withoutButler: number;
}

// 会员生命周期阶段数据接口
interface LifecycleData {
  stage: string;
  withButler: number;
  withoutButler: number;
}

// 留存率数据接口
interface RetentionData {
  period: string;
  rate: number;
  type: string;
}

// 模拟会员留存数据
const mockMemberRetention: MemberRetention[] = [
  {
    id: 'M001',
    name: '张三',
    avatar: 'https://xsgames.co/randomusers/avatar.php?g=male',
    level: '黑钻卡',
    bindingStatus: 'bound',
    butler: '李管家',
    bindTime: '2023-01-15',
    firstOrderDate: '2022-10-05',
    lastOrderDate: '2023-06-10',
    totalOrders: 14,
    totalSpent: 28500,
    repeatPurchaseRate: 85,
    ltv: 68000,
    retentionScore: 92,
    churnRisk: 'low',
    nextPredictedOrder: '2023-07-12',
    inactivityDays: 21,
  },
  {
    id: 'M002',
    name: '李四',
    avatar: 'https://xsgames.co/randomusers/avatar.php?g=male',
    level: '铂金卡',
    bindingStatus: 'bound',
    butler: '王管家',
    bindTime: '2023-02-20',
    firstOrderDate: '2022-11-12',
    lastOrderDate: '2023-05-25',
    totalOrders: 9,
    totalSpent: 16800,
    repeatPurchaseRate: 78,
    ltv: 42000,
    retentionScore: 85,
    churnRisk: 'low',
    nextPredictedOrder: '2023-07-05',
    inactivityDays: 37,
  },
  {
    id: 'M003',
    name: '王五',
    avatar: 'https://xsgames.co/randomusers/avatar.php?g=male',
    level: '金卡',
    bindingStatus: 'bound',
    butler: '张管家',
    bindTime: '2023-03-10',
    firstOrderDate: '2022-12-15',
    lastOrderDate: '2023-06-15',
    totalOrders: 7,
    totalSpent: 12500,
    repeatPurchaseRate: 72,
    ltv: 28000,
    retentionScore: 78,
    churnRisk: 'low',
    nextPredictedOrder: '2023-07-20',
    inactivityDays: 16,
  },
  {
    id: 'M004',
    name: '赵六',
    avatar: 'https://xsgames.co/randomusers/avatar.php?g=male',
    level: '银卡',
    bindingStatus: 'bound',
    butler: '刘管家',
    bindTime: '2023-04-05',
    firstOrderDate: '2023-01-20',
    lastOrderDate: '2023-06-02',
    totalOrders: 5,
    totalSpent: 6800,
    repeatPurchaseRate: 65,
    ltv: 15000,
    retentionScore: 72,
    churnRisk: 'medium',
    nextPredictedOrder: '2023-07-15',
    inactivityDays: 29,
  },
  {
    id: 'M005',
    name: '钱七',
    avatar: 'https://xsgames.co/randomusers/avatar.php?g=female',
    level: '普卡',
    bindingStatus: 'bound',
    butler: '赵管家',
    bindTime: '2023-05-15',
    firstOrderDate: '2023-02-10',
    lastOrderDate: '2023-06-05',
    totalOrders: 3,
    totalSpent: 3200,
    repeatPurchaseRate: 58,
    ltv: 8500,
    retentionScore: 68,
    churnRisk: 'medium',
    nextPredictedOrder: '2023-07-25',
    inactivityDays: 26,
  },
  {
    id: 'M006',
    name: '孙八',
    avatar: 'https://xsgames.co/randomusers/avatar.php?g=female',
    level: '银卡',
    bindingStatus: 'unbound',
    firstOrderDate: '2023-01-05',
    lastOrderDate: '2023-04-12',
    totalOrders: 4,
    totalSpent: 5500,
    repeatPurchaseRate: 42,
    ltv: 12000,
    retentionScore: 55,
    churnRisk: 'high',
    inactivityDays: 80,
  },
  {
    id: 'M007',
    name: '周九',
    avatar: 'https://xsgames.co/randomusers/avatar.php?g=male',
    level: '普卡',
    bindingStatus: 'unbound',
    firstOrderDate: '2023-02-18',
    lastOrderDate: '2023-03-25',
    totalOrders: 2,
    totalSpent: 2800,
    repeatPurchaseRate: 35,
    ltv: 6000,
    retentionScore: 48,
    churnRisk: 'high',
    inactivityDays: 98,
  },
];

// 模拟复购率趋势数据
const mockRepurchaseTrend: RepurchaseTrend[] = [
  { month: '2023-01', withButler: 72, withoutButler: 38 },
  { month: '2023-02', withButler: 75, withoutButler: 42 },
  { month: '2023-03', withButler: 78, withoutButler: 45 },
  { month: '2023-04', withButler: 82, withoutButler: 48 },
  { month: '2023-05', withButler: 85, withoutButler: 50 },
  { month: '2023-06', withButler: 88, withoutButler: 52 },
];

// 模拟生命周期阶段数据
const mockLifecycleData: LifecycleData[] = [
  { stage: '获取', withButler: 100, withoutButler: 100 },
  { stage: '活跃', withButler: 92, withoutButler: 68 },
  { stage: '转化', withButler: 85, withoutButler: 52 },
  { stage: '保留', withButler: 78, withoutButler: 45 },
  { stage: '忠诚', withButler: 72, withoutButler: 32 },
  { stage: '拓展', withButler: 65, withoutButler: 25 },
];

// 模拟留存率数据
const mockRetentionData: RetentionData[] = [
  { period: '7天', rate: 95, type: '绑定管家会员' },
  { period: '30天', rate: 88, type: '绑定管家会员' },
  { period: '60天', rate: 82, type: '绑定管家会员' },
  { period: '90天', rate: 75, type: '绑定管家会员' },
  { period: '180天', rate: 68, type: '绑定管家会员' },
  { period: '365天', rate: 62, type: '绑定管家会员' },
  { period: '7天', rate: 75, type: '普通会员' },
  { period: '30天', rate: 62, type: '普通会员' },
  { period: '60天', rate: 55, type: '普通会员' },
  { period: '90天', rate: 48, type: '普通会员' },
  { period: '180天', rate: 42, type: '普通会员' },
  { period: '365天', rate: 35, type: '普通会员' },
];

const RepurchaseRetentionAnalysis: React.FC = () => {
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [activeTab, setActiveTab] = useState('repurchase');
  const [memberLevel, setMemberLevel] = useState('all');
  const [bindingStatus, setBindingStatus] = useState('all');
  const [churnRisk, setChurnRisk] = useState('all');

  // 根据筛选条件过滤数据
  const getFilteredMemberData = () => {
    let filtered = [...mockMemberRetention];
    
    if (memberLevel !== 'all') {
      filtered = filtered.filter(member => member.level === memberLevel);
    }
    
    if (bindingStatus !== 'all') {
      filtered = filtered.filter(member => 
        (bindingStatus === 'bound' && member.bindingStatus === 'bound') || 
        (bindingStatus === 'unbound' && member.bindingStatus === 'unbound')
      );
    }
    
    if (churnRisk !== 'all') {
      filtered = filtered.filter(member => member.churnRisk === churnRisk);
    }
    
    return filtered;
  };

  // 表格列定义
  const columns: ColumnsType<MemberRetention> = [
    {
      title: '会员信息',
      key: 'info',
      fixed: 'left',
      width: 180,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar src={record.avatar} size="large" style={{ marginRight: 10 }} />
          <div>
            <Text strong>{record.name}</Text>
            <div>
              <Tag color={
                record.level === '黑钻卡' ? 'black' : 
                record.level === '铂金卡' ? 'purple' : 
                record.level === '金卡' ? 'gold' : 
                record.level === '银卡' ? 'default' : 'blue'
              }>
                {record.level}
              </Tag>
              {record.bindingStatus === 'bound' ? (
                <Tag color="green">已绑定</Tag>
              ) : (
                <Tag color="default">未绑定</Tag>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: '管家信息',
      key: 'butler',
      width: 120,
      render: (_, record) => (
        <div>
          {record.bindingStatus === 'bound' && record.butler ? (
            <>
              <div>{record.butler}</div>
              <div><Text type="secondary">绑定: {record.bindTime}</Text></div>
            </>
          ) : (
            <Text type="secondary">未绑定管家</Text>
          )}
        </div>
      ),
    },
    {
      title: '消费情况',
      key: 'consumption',
      width: 160,
      render: (_, record) => (
        <div>
          <div>总订单: {record.totalOrders}单</div>
          <div>总消费: ¥{record.totalSpent.toLocaleString()}</div>
          <div>首次: {record.firstOrderDate}</div>
          <div>最近: {record.lastOrderDate}</div>
        </div>
      ),
    },
    {
      title: '复购率',
      key: 'repeatPurchaseRate',
      width: 150,
      sorter: (a, b) => a.repeatPurchaseRate - b.repeatPurchaseRate,
      render: (_, record) => (
        <div>
          <Text strong>{record.repeatPurchaseRate}%</Text>
          <Progress 
            percent={record.repeatPurchaseRate} 
            size="small" 
            strokeColor={record.repeatPurchaseRate >= 70 ? '#52c41a' : record.repeatPurchaseRate >= 45 ? '#faad14' : '#ff4d4f'} 
          />
        </div>
      ),
    },
    {
      title: '留存评分',
      key: 'retentionScore',
      width: 150,
      sorter: (a, b) => a.retentionScore - b.retentionScore,
      render: (_, record) => (
        <div>
          <Text strong>{record.retentionScore}</Text>
          <Progress 
            percent={record.retentionScore} 
            size="small" 
            strokeColor={record.retentionScore >= 75 ? '#52c41a' : record.retentionScore >= 50 ? '#faad14' : '#ff4d4f'} 
          />
        </div>
      ),
    },
    {
      title: '流失风险',
      key: 'churnRisk',
      width: 120,
      filters: [
        { text: '低风险', value: 'low' },
        { text: '中等风险', value: 'medium' },
        { text: '高风险', value: 'high' },
      ],
      onFilter: (value, record) => record.churnRisk === value,
      render: (_, record) => (
        <Tag color={
          record.churnRisk === 'low' ? 'green' : 
          record.churnRisk === 'medium' ? 'orange' : 'red'
        }>
          {record.churnRisk === 'low' ? '低风险' : 
           record.churnRisk === 'medium' ? '中等风险' : '高风险'}
        </Tag>
      ),
    },
    {
      title: '预计下次消费',
      key: 'nextPredicted',
      width: 160,
      render: (_, record) => (
        <div>
          {record.nextPredictedOrder ? (
            <>
              <div>{record.nextPredictedOrder}</div>
              <div>
                <Badge 
                  status={record.inactivityDays > 60 ? 'error' : record.inactivityDays > 30 ? 'warning' : 'success'} 
                  text={`不活跃${record.inactivityDays}天`} 
                />
              </div>
            </>
          ) : (
            <Text type="danger">可能流失</Text>
          )}
        </div>
      ),
    },
    {
      title: '生命周期价值',
      key: 'ltv',
      width: 120,
      sorter: (a, b) => a.ltv - b.ltv,
      render: (_, record) => (
        <div>
          <Text strong>¥{record.ltv.toLocaleString()}</Text>
        </div>
      ),
    },
  ];

  // 渲染复购率趋势图表 - 简化版
  const renderRepurchaseTrendChart = () => {
    if (!mockRepurchaseTrend || mockRepurchaseTrend.length === 0) {
      return <Empty description="暂无复购率趋势数据" />;
    }
    
    // 分组数据以便于表格展示
    const monthSet = new Set(mockRepurchaseTrend.map(item => item.month)); // 获取所有唯一月份
    const periods = Array.from(monthSet);
    
    return (
      <div style={{ height: 300, overflow: 'auto' }}>
        <Table 
          dataSource={periods.map(period => {
            const withButlerData = mockRepurchaseTrend.find(
              item => item.month === period
            );
            return {
              key: period,
              period,
              withButler: withButlerData ? `${withButlerData.withButler}%` : 'N/A',
              withoutButler: withButlerData ? `${withButlerData.withoutButler}%` : 'N/A',
            };
          })}
          columns={[
            { title: '月份', dataIndex: 'period', key: 'period' },
            { 
              title: '绑定管家会员复购率', 
              dataIndex: 'withButler', 
              key: 'withButler',
              render: (text) => <Text strong type="success">{text}</Text>
            },
            { 
              title: '未绑定管家会员复购率', 
              dataIndex: 'withoutButler', 
              key: 'withoutButler',
              render: (text) => <Text type="secondary">{text}</Text>
            },
          ]}
          pagination={false}
          size="small"
        />
        <div style={{ marginTop: 15, textAlign: 'center' }}>
          <LineChartOutlined style={{ fontSize: 16 }} /> <Text type="secondary">趋势图已简化为表格形式</Text>
        </div>
      </div>
    );
  };

  // 渲染会员留存率图表 - 简化版
  const renderRetentionChart = () => {
    if (!mockRetentionData || mockRetentionData.length === 0) {
      return <Empty description="暂无留存率数据" />;
    }
    
    // 分组数据以便于表格展示
    const periodsSet = new Set(mockRetentionData.map(item => item.period)); // 获取所有唯一周期
    const periods = Array.from(periodsSet);
    
    return (
      <div style={{ height: 300, overflow: 'auto' }}>
        <Table 
          dataSource={periods.map(period => {
            const boundData = mockRetentionData.find(
              item => item.period === period && item.type === '绑定管家会员'
            );
            const unboundData = mockRetentionData.find(
              item => item.period === period && item.type === '普通会员'
            );
            
            return {
              key: period,
              period,
              boundRate: boundData ? `${boundData.rate}%` : 'N/A',
              unboundRate: unboundData ? `${unboundData.rate}%` : 'N/A',
            };
          })}
          columns={[
            { title: '留存周期', dataIndex: 'period', key: 'period' },
            { 
              title: '绑定管家会员', 
              dataIndex: 'boundRate', 
              key: 'boundRate',
              render: (text) => <Text strong type="success">{text}</Text>
            },
            { 
              title: '普通会员', 
              dataIndex: 'unboundRate', 
              key: 'unboundRate',
              render: (text) => <Text type="secondary">{text}</Text>
            },
          ]}
          pagination={false}
          size="small"
        />
        <div style={{ marginTop: 15, textAlign: 'center' }}>
          <BarChartOutlined style={{ fontSize: 16 }} /> <Text type="secondary">留存率对比已简化为表格形式</Text>
        </div>
      </div>
    );
  };

  // 渲染会员生命周期图表 - 简化版
  const renderLifecycleChart = () => {
    if (!mockLifecycleData || mockLifecycleData.length === 0) {
      return <Empty description="暂无生命周期数据" />;
    }
    
    return (
      <div style={{ height: 350, overflow: 'auto' }}>
        <Table 
          dataSource={mockLifecycleData.map(item => ({
            key: item.stage,
            stage: item.stage,
            withButler: `${item.withButler}%`,
            withoutButler: `${item.withoutButler}%`,
            difference: `+${item.withButler - item.withoutButler}%`,
          }))}
          columns={[
            { title: '生命周期阶段', dataIndex: 'stage', key: 'stage' },
            { 
              title: '绑定管家会员', 
              dataIndex: 'withButler', 
              key: 'withButler',
              render: (text) => <Text strong type="success">{text}</Text>
            },
            { 
              title: '普通会员', 
              dataIndex: 'withoutButler', 
              key: 'withoutButler',
              render: (text) => <Text type="secondary">{text}</Text>
            },
            { 
              title: '提升效果', 
              dataIndex: 'difference', 
              key: 'difference',
              render: (text) => <Text type="success">{text}</Text>
            },
          ]}
          pagination={false}
          size="small"
        />
        <div style={{ marginTop: 15, textAlign: 'center' }}>
          <BarChartOutlined style={{ fontSize: 16 }} /> <Text type="secondary">生命周期对比已简化为表格形式</Text>
        </div>
      </div>
    );
  };

  // 渲染会员价值阶段占比图表 - 简化版
  const renderMemberValueChart = () => {
    const data = [
      { type: '初次消费', value: 15 },
      { type: '成长期', value: 25 },
      { type: '稳定期', value: 35 },
      { type: '忠诚期', value: 20 },
      { type: '衰退期', value: 5 },
    ];
    
    const total = data.reduce((sum, item) => sum + item.value, 0);
    
    return (
      <div style={{ height: 300, overflow: 'auto' }}>
        <Table 
          dataSource={data.map(item => ({
            key: item.type,
            type: item.type,
            value: item.value,
            percentage: `${Math.round((item.value / total) * 100)}%`,
          }))}
          columns={[
            { title: '会员阶段', dataIndex: 'type', key: 'type' },
            { title: '人数占比', dataIndex: 'value', key: 'value' },
            { 
              title: '百分比', 
              dataIndex: 'percentage', 
              key: 'percentage',
              render: (text, record) => (
                <div>
                  <Progress 
                    percent={record.value} 
                    size="small" 
                    format={() => text}
                    strokeColor={
                      record.type === '忠诚期' ? '#52c41a' : 
                      record.type === '稳定期' ? '#1890ff' : 
                      record.type === '成长期' ? '#faad14' : 
                      record.type === '初次消费' ? '#722ed1' : '#f5222d'
                    }
                  />
                </div>
              )
            },
          ]}
          pagination={false}
          size="small"
        />
        <div style={{ marginTop: 15, textAlign: 'center' }}>
          <PieChartOutlined style={{ fontSize: 16 }} /> <Text type="secondary">价值阶段占比已简化为表格形式</Text>
        </div>
      </div>
    );
  };

  // 获取整体复购留存数据统计
  const getOverallStats = () => {
    const filteredData = getFilteredMemberData();
    const boundMembers = filteredData.filter(m => m.bindingStatus === 'bound');
    const unboundMembers = filteredData.filter(m => m.bindingStatus === 'unbound');
    
    const avgRepurchaseRate = boundMembers.length > 0 
      ? Math.round(boundMembers.reduce((sum, item) => sum + item.repeatPurchaseRate, 0) / boundMembers.length)
      : 0;
    
    const avgUnboundRepurchaseRate = unboundMembers.length > 0
      ? Math.round(unboundMembers.reduce((sum, item) => sum + item.repeatPurchaseRate, 0) / unboundMembers.length)
      : 0;
      
    const avgRetentionScore = boundMembers.length > 0
      ? Math.round(boundMembers.reduce((sum, item) => sum + item.retentionScore, 0) / boundMembers.length)
      : 0;
      
    const avgLtv = boundMembers.length > 0
      ? Math.round(boundMembers.reduce((sum, item) => sum + item.ltv, 0) / boundMembers.length)
      : 0;
    
    const churnPreventionRate = 82; // 这里使用一个模拟值
    
    return {
      avgRepurchaseRate,
      avgUnboundRepurchaseRate,
      repurchaseDiff: avgRepurchaseRate - avgUnboundRepurchaseRate,
      avgRetentionScore,
      avgLtv,
      churnPreventionRate
    };
  };

  const stats = getOverallStats();

  return (
    <div className="repurchase-retention-analysis">
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Title level={4} style={{ margin: 0 }}>复购与留存分析中心</Title>
          </Col>
          <Col flex="auto">
            <div style={{ float: 'right' }}>
              <Space>
                <Select
                  placeholder="会员等级"
                  defaultValue="all"
                  style={{ width: 120 }}
                  onChange={(value) => setMemberLevel(value)}
                  options={[
                    { value: 'all', label: '全部等级' },
                    { value: '黑钻卡', label: '黑钻卡' },
                    { value: '铂金卡', label: '铂金卡' },
                    { value: '金卡', label: '金卡' },
                    { value: '银卡', label: '银卡' },
                    { value: '普卡', label: '普卡' },
                  ]}
                />
                <Select
                  placeholder="绑定状态"
                  defaultValue="all"
                  style={{ width: 120 }}
                  onChange={(value) => setBindingStatus(value)}
                  options={[
                    { value: 'all', label: '全部状态' },
                    { value: 'bound', label: '已绑定' },
                    { value: 'unbound', label: '未绑定' },
                  ]}
                />
                <Select
                  placeholder="流失风险"
                  defaultValue="all"
                  style={{ width: 120 }}
                  onChange={(value) => setChurnRisk(value)}
                  options={[
                    { value: 'all', label: '全部风险' },
                    { value: 'low', label: '低风险' },
                    { value: 'medium', label: '中等风险' },
                    { value: 'high', label: '高风险' },
                  ]}
                />
                <RangePicker onChange={(_, dateStrings) => setDateRange(dateStrings as [string, string] | null)} />
                <Button icon={<ReloadOutlined />}>刷新数据</Button>
                <Button type="primary" icon={<ExportOutlined />}>导出报告</Button>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="绑定管家会员复购率"
              value={stats.avgRepurchaseRate}
              suffix="%"
              valueStyle={{ color: '#3f8600' }}
              prefix={<RiseOutlined />}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">比未绑定会员高 <Text type="success">+{stats.repurchaseDiff}%</Text></Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="会员留存评分"
              value={stats.avgRetentionScore}
              valueStyle={{ color: '#1890ff' }}
              prefix={<HeartOutlined />}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">满分100，绑定管家会员平均值</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="平均生命周期价值"
              value={stats.avgLtv}
              prefix="¥"
              precision={0}
              valueStyle={{ color: '#cf1322' }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">绑定管家会员的预计LTV</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="流失干预成功率"
              value={stats.churnPreventionRate}
              suffix="%"
              valueStyle={{ color: '#722ed1' }}
              prefix={<CheckCircleOutlined />}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">通过管家干预挽回流失会员</Text>
            </div>
          </Card>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="复购率分析" key="repurchase">
          <Card>
            <Alert
              message="管家绑定显著提升复购率"
              description="数据显示，绑定管家后会员的复购率平均提升35个百分点。高级会员（黑钻卡、铂金卡）的复购率提升尤为明显，达到42%。"
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Row gutter={16}>
              <Col span={12}>
                <Title level={5}>管家绑定前后复购率对比</Title>
                {renderRepurchaseTrendChart()}
              </Col>
              <Col span={12}>
                <Card title="会员复购亮点数据" bordered={false}>
                  <List
                    dataSource={[
                      { title: '高级会员复购率', value: '92%', diff: '+35%' },
                      { title: '平均复购周期缩短', value: '18天', diff: '-42%' },
                      { title: '客单价提升', value: '¥320', diff: '+28%' },
                      { title: '季度连续消费率', value: '78%', diff: '+45%' },
                      { title: '推荐活动参与率', value: '65%', diff: '+52%' },
                    ]}
                    renderItem={(item) => (
                      <List.Item>
                        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                          <Text>{item.title}</Text>
                          <div>
                            <Text strong style={{ marginRight: 16 }}>{item.value}</Text>
                            <Text type="success">{item.diff}</Text>
                          </div>
                        </div>
                      </List.Item>
                    )}
                  />
                </Card>
              </Col>
            </Row>
            
            <Divider />
            
            <Title level={5}>会员复购明细列表</Title>
            <Table
              dataSource={getFilteredMemberData()}
              columns={columns}
              rowKey="id"
              scroll={{ x: 1200 }}
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </TabPane>
        
        <TabPane tab="会员留存分析" key="retention">
          <Card>
            <Title level={5}>会员留存率对比 <Tooltip title="不同时间段会员留存率对比，管家服务显著提升了各阶段留存"><InfoCircleOutlined /></Tooltip></Title>
            {renderRetentionChart()}
            
            <Divider />
            
            <Row gutter={16}>
              <Col span={12}>
                <Card title="会员价值阶段占比" bordered={false}>
                  {renderMemberValueChart()}
                </Card>
              </Col>
              <Col span={12}>
                <Card title="会员留存干预策略" bordered={false}>
                  <Timeline mode="left">
                    <Timeline.Item color="green" label="初次消费">
                      管家主动联系会员表达欢迎和关注，介绍酒店特色服务，建立个人偏好档案。
                    </Timeline.Item>
                    <Timeline.Item color="blue" label="30天留存">
                      发送个性化福利券，针对首次消费偏好推荐相关服务，提供专属折扣。
                    </Timeline.Item>
                    <Timeline.Item color="blue" label="60天留存">
                      邀请参与会员专属活动，管家提供一对一咨询服务，记录最新偏好变化。
                    </Timeline.Item>
                    <Timeline.Item color="blue" label="90天留存">
                      提供季度消费回顾，赠送会员专属礼品，管家定期电话回访。
                    </Timeline.Item>
                    <Timeline.Item color="red" label="沉默期预警">
                      发现会员超过设定时间未消费，管家主动联系了解原因，提供定制化挽留方案。
                    </Timeline.Item>
                  </Timeline>
                </Card>
              </Col>
            </Row>
          </Card>
        </TabPane>
        
        <TabPane tab="生命周期价值分析" key="ltv">
          <Card>
            <Row gutter={16}>
              <Col span={12}>
                <Title level={5}>会员生命周期阶段对比</Title>
                {renderLifecycleChart()}
              </Col>
              <Col span={12}>
                <Card title="管家对LTV的影响" bordered={false}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title="绑定管家会员平均LTV"
                        value={42800}
                        prefix="¥"
                        precision={0}
                      />
                      <div style={{ height: 8 }}></div>
                      <Progress percent={85} size="small" />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title="普通会员平均LTV"
                        value={18500}
                        prefix="¥"
                        precision={0}
                      />
                      <div style={{ height: 8 }}></div>
                      <Progress percent={37} size="small" />
                    </Col>
                  </Row>
                  
                  <Divider />
                  
                  <Title level={5}>LTV提升因素分析</Title>
                  <List
                    dataSource={[
                      { factor: '消费频次提升', weight: 30 },
                      { factor: '客单价增长', weight: 25 },
                      { factor: '品类扩展', weight: 20 },
                      { factor: '活动参与', weight: 15 },
                      { factor: '口碑推荐', weight: 10 },
                    ]}
                    renderItem={(item) => (
                      <List.Item>
                        <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>{item.factor}</div>
                          <div style={{ width: 200 }}>
                            <Progress percent={item.weight} size="small" />
                          </div>
                          <div style={{ width: 60, textAlign: 'right' }}>
                            <Text>{item.weight}%</Text>
                          </div>
                        </div>
                      </List.Item>
                    )}
                  />
                </Card>
              </Col>
            </Row>
            
            <Divider />
            
            <Row gutter={16}>
              <Col span={24}>
                <Card title="流失风险预警与干预" bordered={false}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Card title="低风险会员 (85%)" type="inner" bordered>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Badge status="success" text="近30天内有消费" />
                          <Badge status="success" text="消费频率稳定或上升" />
                          <Badge status="success" text="多品类消费" />
                          <Badge status="success" text="对管家服务评价良好" />
                          <Divider />
                          <Text strong>干预策略:</Text>
                          <Text>定期关怀，提供个性化推荐，邀请参与高端活动</Text>
                        </Space>
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card title="中等风险会员 (10%)" type="inner" bordered>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Badge status="warning" text="30-60天无消费" />
                          <Badge status="warning" text="消费频率下降" />
                          <Badge status="warning" text="消费品类收窄" />
                          <Badge status="warning" text="对服务有一定不满" />
                          <Divider />
                          <Text strong>干预策略:</Text>
                          <Text>管家主动联系了解原因，提供专属优惠，针对性解决问题</Text>
                        </Space>
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card title="高风险会员 (5%)" type="inner" bordered>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Badge status="error" text="60天以上无消费" />
                          <Badge status="error" text="投诉未妥善解决" />
                          <Badge status="error" text="不再响应营销活动" />
                          <Badge status="error" text="竞争对手消费增加" />
                          <Divider />
                          <Text strong>干预策略:</Text>
                          <Text>高级管家一对一沟通，提供重要客户挽留方案，解决核心痛点</Text>
                        </Space>
                      </Card>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default RepurchaseRetentionAnalysis; 